import { useEffect, useMemo, useState } from 'react';
import { useIntl } from 'react-intl';
import { Spinner } from '../../shared/ui/spinner';
import { Cell, Pie, PieChart, ResponsiveContainer, Tooltip } from 'recharts';
import { Calendar, Plus, TrendingDown, TrendingUp, X } from 'lucide-react';
import DatePicker from 'react-datepicker';
import 'react-datepicker/dist/react-datepicker.css';
import { endOfMonth, format, startOfMonth, subDays, subMonths } from 'date-fns';
import styles from './Transactions.module.css';
import { datePickerLocale } from '../../shared/i18n/date-picker-locale';
import { useAuth } from '../../context/AuthContext';
import { useOverviewCurrency } from '../../context/OverviewCurrencyContext';
import { useOverviewRefresh } from '../../context/OverviewRefreshContext';
import { getOverviewRequest } from '../dashbord/api/index';
import correctio from '../../shared/assets/icons/correctio.png';
import AddRecordModal from '../../components/AddRecordModal/AddRecordModal';
import {
	formatDateOnlyToDDMMYYYY,
	sortTransactionsNewestFirst,
} from '../../shared/lib/date/date-utils';

// Import icons
import CashCoin from '../../shared/assets/categories/CashCoin.png';
import GlassMinus from '../../shared/assets/categories/GlassMinus.png';
import StudentRegular from '../../shared/assets/categories/StudentRegular.png';
import ServingFood from '../../shared/assets/categories/ServingFood.png';
import HouseLineRegular from '../../shared/assets/categories/HouseLineRegular.png';
import SecurityValidation from '../../shared/assets/categories/SecurityValidation.png';
import EmojiFunnySquare from '../../shared/assets/categories/EmojiFunnySquare.png';
import Healtcare from '../../shared/assets/categories/Healtcare.png';
import Shopping from '../../shared/assets/categories/ShoppingBag.png';
import Taxes from '../../shared/assets/categories/Taxes.png';
import ConvertRange from '../../shared/assets/categories/ScisTransparentSupply.png';
import SelfTransfer from '../../shared/assets/categories/SelfTransfer.png';
import HomeBolt from '../../shared/assets/categories/HomeBolt.png';
import Healthcare from '../../shared/assets/categories/Healthcare.png';
import Gift from '../../shared/assets/categories/Gift.png';
import DatabaseDollar from '../../shared/assets/categories/DatabaseDollar.png';
import ReportMoney from '../../shared/assets/categories/ReportMoney.png';
import DeviceDesktopDollar from '../../shared/assets/categories/DeviceDesktopDollar.png';
import HomeDollar from '../../shared/assets/categories/HomeDollar.png';
import MoneybagPlus from '../../shared/assets/categories/MoneybagPlus.png';
import UserDollar from '../../shared/assets/categories/UserDollar.png';

import { Filter } from '../../shared/assets/icons';

import { getIncomesRequest, incomesGerCategoriesRequest } from '../Income/api';
import {
	expensesGerCategoriesRequest,
	getExpensesRequest,
} from '../Expenses/api';
import {
	buildTransactionsExpenseParams,
	buildTransactionsIncomeParams,
} from './listParams';

// Currency symbol helper function
const getCurrencySymbol = currency => {
	const symbols = {
		USD: '$',
		EUR: '€',
		AMD: '֏',
		RUB: '₽',
	};
	return symbols[currency] || currency;
};

const Transactions = () => {
	const intl = useIntl();
	const { userInfo } = useAuth();
	const { currency: overviewCurrency } = useOverviewCurrency();
	const [isModalOpen, setIsModalOpen] = useState(false);
	const [activeFilters, setActiveFilters] = useState(['All']);
	const [showFilterModal, setShowFilterModal] = useState(false);
	const [selectedCategories, setSelectedCategories] = useState(['All']);
	const [selectedFrequency, setSelectedFrequency] = useState('');
	const [selectedType, setSelectedType] = useState('All');
	const [dateRange, setDateRange] = useState({
		from: null,
		to: null,
	});
	const [committedCategoryNames, setCommittedCategoryNames] = useState(['All']);
	const [committedDateRange, setCommittedDateRange] = useState({
		from: null,
		to: null,
	});
	const [committedType, setCommittedType] = useState('All');
	const [listFetchKey, setListFetchKey] = useState(0);
	const [transactions, setTransactions] = useState([]);
	const [loading, setLoading] = useState(true);
	const [totalBalance, setTotalBalance] = useState(0);
	const [totalIncome, setTotalIncome] = useState(0);
	const [totalExpense, setTotalExpense] = useState(0);
	const [incomeCategories, setIncomeCategories] = useState([]);
	const [expenseCategories, setExpenseCategories] = useState([]);
	const [allCategories, setAllCategories] = useState([
		// { name: "All", original_name: "All" },
	]);
	const [refreshTrigger, setRefreshTrigger] = useState(0);
	const [hiddenChartTypes, setHiddenChartTypes] = useState([]);
	// State for summary card
	const [userCurrency, setUserCurrency] = useState('USD');
	const [currentMonthBalance, setCurrentMonthBalance] = useState(0);
	const [monthComparison, setMonthComparison] = useState({
		percentage: null,
		trend: null,
		message: 'No comparison yet',
	});

	const frequencies = ['Weekly', 'Monthly', 'Yearly', 'One Time'];
	const transactionTypes = ['All', 'Income', 'Expense'];
	const typeLabels = {
		All: intl.formatMessage({ id: 'TransactionsID30' }), // կամ ուղղակի "All"
		Income: intl.formatMessage({ id: 'TransactionsID31' }),
		Expense: intl.formatMessage({ id: 'TransactionsID32' }),
	};
	// Function to get category by ID
	const getCategoryById = (categoryId, categoriesArray) => {
		if (!categoryId) return null;
		return (
			categoriesArray.find(
				cat => cat.id === categoryId || cat._id === categoryId,
			) || null
		);
	};

	// Function to get category name by ID
	const getCategoryNameById = (categoryId, categoriesArray) => {
		const category = getCategoryById(categoryId, categoriesArray);
		return category?.name || 'Other';
	};

	// Icon mapping function
	const getCategoryIcon = categoryName => {
		const iconMap = {
			// Expense categories
			shopping: Shopping,
			taxes: Taxes,
			transportation: ConvertRange,
			travel: SelfTransfer,
			utilities: HomeBolt,
			healthcare: Healthcare,
			'gifts & donations': Gift,
			education: StudentRegular,
			'food & dining': ServingFood,
			housing: HouseLineRegular,
			insurance: SecurityValidation,
			'personal care': Healtcare,
			entertainment: EmojiFunnySquare,

			// Income categories
			business: DatabaseDollar,
			investment: ReportMoney,
			freelance: DeviceDesktopDollar,
			rental: HomeDollar,
			salary: MoneybagPlus,
			other: UserDollar,

			// Additional categories
			'food & dining': ServingFood,
			'personal care': Healtcare,
			'gifts & donations': Gift,
		};

		return iconMap[categoryName?.toLowerCase()] || CashCoin;
	};

	const refreshData = () => {
		setRefreshTrigger(prev => prev + 1);
	};

	const { setRefreshCallback } = useOverviewRefresh();
	useEffect(() => {
		setRefreshCallback(refreshData);
		return () => setRefreshCallback(null);
	}, [setRefreshCallback, refreshData]);

	// Calculate current month balance from overview data
	const calculateCurrentMonthBalance = (overviewData, currency) => {
		if (
			!overviewData ||
			!overviewData.data ||
			!Array.isArray(overviewData.data)
		) {
			return 0;
		}

		// Get current month in YYYY-MM format
		const now = new Date();
		const currentMonth = `${now.getFullYear()}-${String(now.getMonth() + 1).padStart(2, '0')}`;

		// Filter for current month and selected currency
		const currentMonthData = overviewData.data.find(
			item => item.currency === currency && item.month === currentMonth,
		);

		if (!currentMonthData) {
			return 0;
		}

		const income = Number(currentMonthData.income) || 0;
		const expenses = Number(currentMonthData.expenses) || 0;
		return income - expenses;
	};

	// Calculate previous month balance from overview data
	const calculatePreviousMonthBalance = (overviewData, currency) => {
		if (
			!overviewData ||
			!overviewData.data ||
			!Array.isArray(overviewData.data)
		) {
			return 0;
		}

		// Get previous month in YYYY-MM format
		const now = new Date();
		const previousMonthDate = subMonths(now, 1);
		const previousMonth = `${previousMonthDate.getFullYear()}-${String(previousMonthDate.getMonth() + 1).padStart(2, '0')}`;

		// Filter for previous month and selected currency
		const previousMonthData = overviewData.data.find(
			item => item.currency === currency && item.month === previousMonth,
		);

		if (!previousMonthData) {
			return 0;
		}

		const income = Number(previousMonthData.income) || 0;
		const expenses = Number(previousMonthData.expenses) || 0;
		return income - expenses;
	};

	// Calculate month-over-month comparison
	const calculateMonthComparison = (current, previous) => {
		if (previous === 0 || previous === null || previous === undefined) {
			return {
				percentage: null,
				trend: null,
				message: intl.formatMessage({ id: 'FinPathDashboardCartID10' }),
			};
		}

		const percentage = ((current - previous) / Math.abs(previous)) * 100;
		const trend = percentage >= 0 ? 'up' : 'down';
		const absPercentage = Math.abs(percentage).toFixed(1);

		return {
			percentage: absPercentage,
			trend,
			message: `${absPercentage}%  ${intl.formatMessage({ id: 'FinPathDashboardCartID11' })}`,
		};
	};

	// Fetch overview data for summary card (use sidebar-header dropdown currency)
	useEffect(() => {
		const fetchOverviewData = async () => {
			try {
				const currency =
					overviewCurrency || userInfo?.currency_preference || 'USD';
				setUserCurrency(currency);

				// Fetch overview data for selected currency
				const overviewResponse = await getOverviewRequest(null, null, currency);

				// Calculate current month balance
				const currentBalance = calculateCurrentMonthBalance(
					overviewResponse,
					currency,
				);
				setCurrentMonthBalance(currentBalance);

				// Calculate previous month balance
				const previousBalance = calculatePreviousMonthBalance(
					overviewResponse,
					currency,
				);

				// Calculate comparison
				const comparison = calculateMonthComparison(
					currentBalance,
					previousBalance,
				);
				setMonthComparison(comparison);
			} catch (error) {
				console.error('Failed to fetch overview data:', error);
				// Set defaults on error
				setCurrentMonthBalance(0);
				setMonthComparison({
					percentage: null,
					trend: null,
					message: intl.formatMessage({ id: 'FinPathDashboardCartID10' }),
				});
			}
		};

		fetchOverviewData();
	}, [overviewCurrency, userInfo, refreshTrigger]);

	useEffect(() => {
		const fetchData = async () => {
			try {
				setLoading(true);

				const [incomeCategoriesResponse, expenseCategoriesResponse] =
					await Promise.all([
						incomesGerCategoriesRequest(),
						expensesGerCategoriesRequest(),
					]);

				const incomeCatsData =
					incomeCategoriesResponse?.categories ||
					incomeCategoriesResponse?.data ||
					incomeCategoriesResponse ||
					[];

				const expenseCatsData =
					expenseCategoriesResponse?.categories ||
					expenseCategoriesResponse?.data ||
					expenseCategoriesResponse ||
					[];

				const incomeParams = buildTransactionsIncomeParams(
					incomeCatsData,
					committedCategoryNames,
					committedDateRange,
					committedType,
				);
				const expenseParams = buildTransactionsExpenseParams(
					expenseCatsData,
					committedCategoryNames,
					committedDateRange,
					committedType,
				);

				const incomesPromise =
					incomeParams === null
						? Promise.resolve({ incomes: [] })
						: getIncomesRequest(incomeParams);
				const expensesPromise =
					expenseParams === null
						? Promise.resolve({ expenses: [] })
						: getExpensesRequest(expenseParams);

				const [incomesResponse, expensesResponse] = await Promise.all([
					incomesPromise,
					expensesPromise,
				]);

				const incomeCatNames = incomeCatsData
					.map(cat => cat.name || cat)
					.filter(Boolean);

				const expenseCatNames = expenseCatsData
					.map(cat => cat.name || cat)
					.filter(Boolean);

				setIncomeCategories(incomeCatsData);
				setExpenseCategories(expenseCatsData);

				const categoryById = new Map();
				incomeCatsData.forEach(c =>
					categoryById.set(c.id || c._id, {
						...c,
						name: c.name,
						original_name: c.original_name ?? c.name,
					}),
				);
				expenseCatsData.forEach(c =>
					categoryById.set(c.id || c._id, {
						...c,
						name: c.name,
						original_name: c.original_name ?? c.name,
					}),
				);
				const combinedCategoryObjects = [
					{ name: 'All', original_name: 'All' },
					...Array.from(categoryById.values()),
				];
				setAllCategories(combinedCategoryObjects);

				const incomesData =
					incomesResponse?.incomes ||
					incomesResponse?.data ||
					incomesResponse ||
					[];

				const expensesData =
					expensesResponse?.expenses ||
					expensesResponse?.data ||
					expensesResponse ||
					[];

				const totalIncomeAmount = incomesData.reduce(
					(sum, item) => sum + (Number(item.amount) || 0),
					0,
				);
				const totalExpenseAmount = expensesData.reduce(
					(sum, item) => sum + (Number(item.amount) || 0),
					0,
				);

				setTotalIncome(totalIncomeAmount);
				setTotalExpense(totalExpenseAmount);
				setTotalBalance(totalIncomeAmount - totalExpenseAmount);

				const incomeTransactions = incomesData.map(income => {
					const category = getCategoryById(income.category_id, incomeCatsData);
					const categoryName = category?.name || income.category || 'Other';
					const iconLookupName =
						category?.original_name ||
						category?.name ||
						income.category ||
						'Other';

					return {
						id: income.id || `income-${Math.random()}`,
						category: categoryName,
						currency: income.currency,
						date: formatDateOnlyToDDMMYYYY(income.date),
						transactionDate: income.date,
						createdAt: income.created_at,
						amount: Number(income.amount) || 0,
						icon: getCategoryIcon(iconLookupName),
						type: intl.formatMessage({ id: 'IncomeId01' }),
						color: '#4DB8A8',
					};
				});

				const expenseTransactions = expensesData.map(expense => {
					const category = getCategoryById(
						expense.category_id,
						expenseCatsData,
					);
					const categoryName = category?.name || expense.category || 'Other';
					const iconLookupName =
						category?.original_name ||
						category?.name ||
						expense.category ||
						'Other';

					return {
						id: expense.id || `expense-${Math.random()}`,
						category: categoryName,
						currency: expense.currency,
						date: formatDateOnlyToDDMMYYYY(expense.date),
						transactionDate: expense.date,
						createdAt: expense.created_at,
						amount: Number(expense.amount) || 0,
						icon: getCategoryIcon(iconLookupName),
						type: intl.formatMessage({ id: 'ExpenseId02' }),
						color: '#5B5FCF',
					};
				});

				setTransactions(
					sortTransactionsNewestFirst([
						...incomeTransactions,
						...expenseTransactions,
					]),
				);
			} catch (error) {
				console.error('Failed to load transaction data:', error);
				setTransactions([]);
				setIncomeCategories([]);
				setExpenseCategories([]);
				setAllCategories([{ name: 'All', original_name: 'All' }]);
				setTotalBalance(0);
				setTotalIncome(0);
				setTotalExpense(0);
			} finally {
				setLoading(false);
			}
		};

		fetchData();
	}, [refreshTrigger, listFetchKey]);

	// Derive current month balance and comparison from transactions (card always shows current month vs previous month; use sidebar-header currency)
	const cardData = useMemo(() => {
		const currency = overviewCurrency || userInfo?.currency_preference || 'USD';
		const now = new Date();
		const currentYear = now.getFullYear();
		const currentMonth = String(now.getMonth() + 1).padStart(2, '0');
		const prevMonthDate = subMonths(now, 1);
		const prevYear = prevMonthDate.getFullYear();
		const prevMonth = String(prevMonthDate.getMonth() + 1).padStart(2, '0');

		const isInCurrentMonth = dateStr => {
			const parts = dateStr.split('.');
			if (parts.length !== 3) return false;
			const [day, month, year] = parts;
			return year === String(currentYear) && month === currentMonth;
		};
		const isInPreviousMonth = dateStr => {
			const parts = dateStr.split('.');
			if (parts.length !== 3) return false;
			const [month, year] = parts;
			return year === String(prevYear) && month === prevMonth;
		};

		let currentIncome = 0,
			currentExpense = 0,
			prevIncome = 0,
			prevExpense = 0;

		transactions.forEach(t => {
			if (t.currency !== currency) return;
			if (isInCurrentMonth(t.date)) {
				if (t.type === intl.formatMessage({ id: 'IncomeId01' }))
					currentIncome += t.amount;
				else currentExpense += t.amount;
			} else if (isInPreviousMonth(t.date)) {
				if (t.type === intl.formatMessage({ id: 'IncomeId01' }))
					prevIncome += t.amount;
				else prevExpense += t.amount;
			}
		});

		const currentBalance = currentIncome - currentExpense;
		const previousBalance = prevIncome - prevExpense;

		let message = intl.formatMessage({ id: 'TransactionsID26' });
		let trend = null;
		if (
			previousBalance !== 0 &&
			previousBalance !== null &&
			previousBalance !== undefined
		) {
			const percentage =
				((currentBalance - previousBalance) / Math.abs(previousBalance)) * 100;
			trend = percentage >= 0 ? 'up' : 'down';
			message = `${Math.abs(percentage).toFixed(1)}%   ${intl.formatMessage({ id: 'FinPathDashboardCartID11' })}`;
		}

		return {
			currentMonthBalance: currentBalance,
			monthComparison: { trend, message },
			userCurrency: currency,
		};
	}, [transactions, overviewCurrency, userInfo?.currency_preference]);

	const handleClearFilters = () => {
		setSelectedCategories(['All']);
		setDateRange({ from: null, to: null });
		setActiveFilters(['All']);
		setSelectedType('All');
		setCommittedCategoryNames(['All']);
		setCommittedDateRange({ from: null, to: null });
		setCommittedType('All');
		setListFetchKey(k => k + 1);
	};

	// Use sidebar-header currency for chart (only show income/expense in selected currency)
	const selectedCurrency =
		overviewCurrency || userInfo?.currency_preference || 'USD';
	const totalsInCurrency = useMemo(() => {
		const income = transactions
			.filter(
				t =>
					t.type === intl.formatMessage({ id: 'IncomeId01' }) &&
					(t.currency || 'USD') === selectedCurrency,
			)
			.reduce((sum, t) => sum + (Number(t.amount) || 0), 0);
		const expense = transactions
			.filter(
				t =>
					t.type === intl.formatMessage({ id: 'ExpenseId02' }) &&
					(t.currency || 'USD') === selectedCurrency,
			)
			.reduce((sum, t) => sum + (Number(t.amount) || 0), 0);

		return { income, expense };
	}, [transactions, selectedCurrency]);

	// Prepare chart data by type (Income/Expense) in selected currency
	const prepareChartDataByType = () => {
		const typeData = [
			{
				id: 'IncomeId01',
				name: intl.formatMessage({ id: 'IncomeId01' }),
				value: totalsInCurrency.income,
				color: '#4cc490',
				icon: MoneybagPlus,
				type: intl.formatMessage({ id: 'IncomeId01' }),
			},
			{
				id: 'ExpenseId02',
				name: intl.formatMessage({ id: 'ExpenseId02' }),
				value: totalsInCurrency.expense,
				color: '#ffb4b4',
				icon: Shopping,
				type: intl.formatMessage({ id: 'ExpenseId02' }),
			},
		];

		// Filter out hidden types
		return typeData.filter(
			item => item.value > 0 && !hiddenChartTypes.includes(item.id),
		);
	};

	const chartDataByType = prepareChartDataByType();

	// Handle legend click to hide/show chart segment
	const handleLegendClick = typeName => {
		setHiddenChartTypes(prev => {
			if (prev.includes(typeName)) {
				return prev.filter(type => type !== typeName);
			} else {
				return [...prev, typeName];
			}
		});
	};

	const removeFilter = filter => {
		if (filter === 'All') {
			setSelectedCategories(['All']);
			setSelectedFrequency('Monthly');
			setSelectedType('All');
			setDateRange({ from: null, to: null });
			setActiveFilters(['All']);
			setHiddenChartTypes([]);
			setCommittedCategoryNames(['All']);
			setCommittedDateRange({ from: null, to: null });
			setCommittedType('All');
			setListFetchKey(k => k + 1);
			return;
		}

		const newActiveFilters = activeFilters.filter(f => f !== filter);
		let nextCategories = [...selectedCategories];
		let nextDateRange = { ...dateRange };
		let nextType = selectedType;

		const allCategoryNames = [...incomeCategories, ...expenseCategories]
			.map(cat => cat.name)
			.filter(Boolean);
		if (allCategoryNames.includes(filter)) {
			nextCategories = selectedCategories.filter(cat => cat !== filter);
			if (nextCategories.length === 0) {
				nextCategories = ['All'];
			}
		}

		if (frequencies.includes(filter)) {
			setSelectedFrequency('Monthly');
		}

		if (transactionTypes.includes(filter) && filter !== 'All') {
			nextType = 'All';
		}

		if (
			filter.includes('→') ||
			/^\d{2}\.\d{2}\.\d{2}$/.test(String(filter).trim())
		) {
			nextDateRange = { from: null, to: null };
		}

		setSelectedCategories(nextCategories);
		setDateRange(nextDateRange);
		setSelectedType(nextType);
		setActiveFilters(newActiveFilters.length > 0 ? newActiveFilters : ['All']);
		setCommittedCategoryNames(nextCategories);
		setCommittedDateRange(nextDateRange);
		setCommittedType(nextType);
		setListFetchKey(k => k + 1);
	};

	const handleCategoryToggle = category => {
		if (category === 'All') {
			if (selectedCategories.includes('All')) {
				setSelectedCategories([]);
			} else {
				const incomeCatNames = incomeCategories.map(cat => cat.name);
				const expenseCatNames = expenseCategories.map(cat => cat.name);
				const allCategoryNames = [...incomeCatNames, ...expenseCatNames].filter(
					Boolean,
				);
				setSelectedCategories(['All', ...allCategoryNames]);
			}
		} else {
			setSelectedCategories(prev => {
				const includesAll = prev.includes('All');

				const newCategories = prev.includes(category)
					? prev.filter(cat => cat !== category && cat !== 'All')
					: [...prev.filter(cat => cat !== 'All'), category];

				const incomeCatNames = incomeCategories.map(cat => cat.name);
				const expenseCatNames = expenseCategories.map(cat => cat.name);
				const allCategoryNames = [...incomeCatNames, ...expenseCatNames].filter(
					Boolean,
				);
				const allSelected = allCategoryNames.every(cat =>
					newCategories.includes(cat),
				);

				if (allSelected) {
					return ['All', ...allCategoryNames];
				}

				return newCategories;
			});
		}
	};

	const handleTypeSelect = type => {
		setSelectedType(type);
	};

	const handleFrequencySelect = frequency => {
		setSelectedFrequency(frequency);

		// if (frequency === "One Time") {
		//   setDateRange({ from: null, to: null });
		//   return;
		// }

		const today = new Date();
		let fromDate = null;
		let toDate = today;

		switch (frequency) {
			case 'Weekly':
				fromDate = subDays(today, 7);
				break;
			case 'Monthly':
				fromDate = startOfMonth(today);
				toDate = endOfMonth(today);
				break;
			case 'Yearly':
				fromDate = new Date(today.getFullYear(), 0, 1);
				toDate = new Date(today.getFullYear(), 11, 31);
				break;
			default:
				fromDate = startOfMonth(today);
				toDate = endOfMonth(today);
		}

		setDateRange({ from: fromDate, to: toDate });
	};

	const handleApplyFilters = () => {
		const newFilters = [];

		// Add category filters
		if (selectedCategories.includes('All')) {
			newFilters.push('All');
		} else if (selectedCategories.length > 0) {
			const selectedCats = selectedCategories.filter(cat => cat !== 'All');
			newFilters.push(...selectedCats);
		}

		// Add type filter
		if (selectedType && selectedType !== 'All') {
			newFilters.push(selectedType);
		}

		// Add frequency filter
		if (selectedFrequency && selectedFrequency !== 'Monthly') {
			newFilters.push(selectedFrequency);
		}

		// Add date range filter
		if (dateRange.from && dateRange.to) {
			const fromStr = format(dateRange.from, 'dd.MM.yy');
			const toStr = format(dateRange.to, 'dd.MM.yy');
			if (fromStr === toStr) {
				newFilters.push(fromStr);
			} else {
				newFilters.push(`${fromStr} → ${toStr}`);
			}
		}

		setActiveFilters(newFilters.length > 0 ? newFilters : ['All']);
		setCommittedCategoryNames([...selectedCategories]);
		setCommittedDateRange({
			from: dateRange.from,
			to: dateRange.to,
		});
		setCommittedType(selectedType);
		setListFetchKey(k => k + 1);
		setShowFilterModal(false);
	};

	const handleResetFilters = () => {
		setSelectedCategories(['All']);
		// setSelectedFrequency("One Time");
		setSelectedType('All');
		setDateRange({ from: null, to: null });
		setActiveFilters(['All']);
		setHiddenChartTypes([]);
		setCommittedCategoryNames(['All']);
		setCommittedDateRange({ from: null, to: null });
		setCommittedType('All');
		setListFetchKey(k => k + 1);
	};

	const handleDatePreset = preset => {
		const today = new Date();
		let fromDate = null;
		let toDate = today;

		switch (preset) {
			case 'today':
				fromDate = today;
				toDate = today;
				break;
			case 'last7days':
				fromDate = subDays(today, 7);
				break;
			case 'last30days':
				fromDate = subDays(today, 30);
				break;
			case 'thisMonth':
				fromDate = startOfMonth(today);
				toDate = endOfMonth(today);
				break;
			case 'lastMonth':
				const lastMonth = subMonths(today, 1);
				fromDate = startOfMonth(lastMonth);
				toDate = endOfMonth(lastMonth);
				break;
			default:
				break;
		}

		setDateRange({ from: fromDate, to: toDate });
	};

	// Filter transactions based on ALL filters
	// const filteredTransactions = transactions.filter((transaction) => {
	//   // Category filter
	//   if (
	//     !selectedCategories.includes("All") &&
	//     !selectedCategories.includes(transaction.category)
	//   ) {
	//     return false;
	//   }

	//   // Type filter
	//   if (selectedType !== "All" && transaction.type !== selectedType) {

	//     return false;
	//   }

	//   // Date range filter
	//   if (dateRange.from || dateRange.to) {
	//     const parts = transaction.date.split(".");
	//     const transactionDate = new Date(`${parts[2]}-${parts[1]}-${parts[0]}`);

	//     const fromDate = dateRange.from
	//       ? startOfDay(new Date(dateRange.from))
	//       : null;
	//     const toDate = dateRange.to ? endOfDay(new Date(dateRange.to)) : null;
	//     const dateMatch =
	//       (!fromDate || transactionDate >= fromDate) &&
	//       (!toDate || transactionDate <= toDate);

	//     if (!dateMatch) {
	//       return false;
	//     }
	//   }

	//   // Frequency filter
	//   if (selectedFrequency && selectedFrequency !== "One Time") {
	//     const transactionDate = new Date(
	//       transaction.date.split(".").reverse().join("-"),
	//     );
	//     const today = new Date();

	//     switch (selectedFrequency) {
	//       case "Weekly":
	//         const weekAgo = subDays(today, 7);
	//         if (transactionDate < weekAgo) {
	//           return false;
	//         }
	//         break;
	//       case "Monthly":
	//         const startOfCurrentMonth = startOfMonth(today);
	//         const endOfCurrentMonth = endOfMonth(today);
	//         if (
	//           transactionDate < startOfCurrentMonth ||
	//           transactionDate > endOfCurrentMonth
	//         ) {
	//           return false;
	//         }
	//         break;
	//       case "Yearly":
	//         const startOfYear = new Date(today.getFullYear(), 0, 1);
	//         const endOfYear = new Date(today.getFullYear(), 11, 31);
	//         if (transactionDate < startOfYear || transactionDate > endOfYear) {
	//           return false;
	//         }
	//         break;
	//     }
	//   }

	//   return true;
	// });

	const filteredTransactions = transactions;
	const totalInCurrencyForTooltip =
		totalsInCurrency.income + totalsInCurrency.expense;

	const CustomTooltip = ({ active, payload }) => {
		if (active && payload && payload.length) {
			const data = payload[0].payload;
			return (
				<div className={styles.customTooltip}>
					<p className={styles.tooltipLabel}>{data.name}</p>
					<p className={styles.tooltipValue}>
						{data.value.toLocaleString()}
						{getCurrencySymbol(cardData.userCurrency)}
					</p>
					<p className={styles.tooltipPercentage}>
						{totalInCurrencyForTooltip > 0
							? `${((data.value / totalInCurrencyForTooltip) * 100).toFixed(1)}%`
							: '0%'}
					</p>
				</div>
			);
		}
		return null;
	};

	if (loading) {
		return <Spinner />;
	}

	return (
		<div className={styles.container}>
			{/* <SidebarHeader /> */}
			<div className={styles.content}>
				<div className={styles.topSection}>
					<div className={styles.summaryCard}>
						<h3 className={styles.summaryTitle}>
							{intl.formatMessage({ id: 'TransactionsID5' })}
						</h3>
						<span className={styles.summarySubtitle}>
							{intl.formatMessage({ id: 'TransactionsID6' })}
						</span>
						<div className={styles.coinIcon}>
							<img src={correctio} alt='correctio' />
						</div>
						<div className={styles.summaryAmount}>
							<span>
								{cardData.currentMonthBalance.toLocaleString()}
								{getCurrencySymbol(cardData.userCurrency)}
							</span>
							{cardData.monthComparison.trend === 'up' ? (
								<TrendingUp size={16} className={styles.trendIcon} />
							) : cardData.monthComparison.trend === 'down' ? (
								<TrendingDown size={16} className={styles.trendIcon} />
							) : null}
						</div>
						<div className={styles.summaryChange}>
							<span className={styles.spanText}>
								{cardData.monthComparison.message}
							</span>
						</div>
					</div>

					<div className={styles.chartContainer}>
						{filteredTransactions.length > 0 ? (
							<div className={styles.chartWrapper}>
								<ResponsiveContainer width='100%' height={250}>
									<PieChart>
										<Pie
											data={chartDataByType}
											cx='50%'
											cy='50%'
											innerRadius={60}
											outerRadius={100}
											paddingAngle={2}
											dataKey='value'
											isAnimationActive={false}
										>
											{chartDataByType.map((entry, index) => (
												<Cell key={`cell-${index}`} fill={entry.color} />
											))}
										</Pie>
										<Tooltip
											content={<CustomTooltip />}
											animationDuration={0}
										/>
									</PieChart>
								</ResponsiveContainer>

								<div className={styles.legendContainer}>
									{[
										{
											id: 'IncomeId01',
											name: intl.formatMessage({ id: 'NavBarAuthId3' }),
											value: totalIncome,
											color: '#4cc490',
										},
										{
											id: 'ExpenseId02',
											name: intl.formatMessage({ id: 'NavBarAuthId4' }),
											value: totalExpense,
											color: '#ffb4b4',
										},
									].map((item, index) => {
										const isHidden = hiddenChartTypes.includes(item.id);
										return (
											<div
												key={index}
												className={styles.legendRow}
												onClick={() => handleLegendClick(item.id)}
												style={{ cursor: 'pointer' }}
											>
												<div
													className={styles.legendColor}
													style={{
														backgroundColor: isHidden ? '#e0e0e0' : item.color,
														opacity: isHidden ? 0.5 : 1,
														position: 'relative',
													}}
												>
													{isHidden && (
														<div className={styles.hiddenIndicator}>×</div>
													)}
												</div>
												<span
													className={`${styles.legendName} ${
														isHidden ? styles.legendNameHidden : ''
													}`}
												>
													{item.name}
												</span>
											</div>
										);
									})}
								</div>
							</div>
						) : (
							<div className={styles.noDataMessage}>
								<p>{intl.formatMessage({ id: 'TransactionsID7' })}</p>
								<img
									className={styles.emptyTableImage}
									src={GlassMinus}
									alt='GlassMinus'
								/>
							</div>
						)}
					</div>
				</div>
				<div className={styles.filtersSection}>
					<div className={styles.filterControls}>
						<button
							className={styles.filterButton}
							onClick={() => setShowFilterModal(true)}
						>
							<Filter />
							<span>{intl.formatMessage({ id: 'TransactionsID8' })}</span>
						</button>
						{activeFilters
							.filter(filter => filter !== 'All')
							.map(filter => (
								<button key={filter} className={styles.filterTag}>
									<span>{filter}</span>
									<X size={16} onClick={() => removeFilter(filter)} />
								</button>
							))}
					</div>
					<button
						className={styles.addButton}
						onClick={() => setIsModalOpen(true)}
					>
						<span>{intl.formatMessage({ id: 'TransactionsID9' })}</span>
						<Plus size={20} />
					</button>
				</div>

				{showFilterModal && (
					<div className={styles.modalOverlay}>
						<div className={styles.modalContent}>
							<div className={styles.modalHeader}>
								<h3>{intl.formatMessage({ id: 'TransactionsID10' })}</h3>
								<button
									className={styles.closeButton}
									onClick={() => setShowFilterModal(false)}
								>
									<X size={20} />
								</button>
							</div>

							<div className={styles.filterSection}>
								<div className={styles.dateSelector}>
									<h4 className={styles.sectionTitle}>
										{intl.formatMessage({ id: 'TransactionsID11' })}
									</h4>

									<div className={styles.dateInputs}>
										<div className={styles.dateInputGroup}>
											<label>
												{intl.formatMessage({ id: 'TransactionsID12' })}
											</label>
											<div className={styles.dateInputWrapper}>
												<DatePicker
													selected={dateRange.from}
													onChange={date => {
														if (dateRange.to && date > dateRange.to) {
															setDateRange({ from: date, to: date });
														} else {
															setDateRange(prev => ({
																...prev,
																from: date,
															}));
														}
													}}
													selectsStart
													startDate={dateRange.from}
													endDate={dateRange.to}
													dateFormat='dd.MM.yy'
													placeholderText={intl.formatMessage({
														id: 'DatePlaceholderId1',
													})}
													locale={datePickerLocale(intl.locale)}
													className={styles.datePickerInput}
													isClearable
													showMonthDropdown
													showYearDropdown
													dropdownMode='scroll'
													scrollableYearDropdown
													yearDropdownItemNumber={15}
													popperPlacement='bottom-start'
													calendarStartDay={1}
												/>
												<Calendar size={16} className={styles.calendarIcon} />
											</div>
										</div>
										<div className={styles.dateInputGroup}>
											<label>
												{intl.formatMessage({ id: 'TransactionsID13' })}
											</label>
											<div className={styles.dateInputWrapper}>
												<DatePicker
													selected={dateRange.to}
													onChange={date => {
														if (dateRange.from && date < dateRange.from) {
															setDateRange({ from: date, to: date });
														} else {
															setDateRange(prev => ({
																...prev,
																to: date,
															}));
														}
													}}
													selectsEnd
													startDate={dateRange.from}
													endDate={dateRange.to}
													minDate={dateRange.from}
													dateFormat='dd.MM.yy'
													placeholderText={intl.formatMessage({
														id: 'DatePlaceholderId1',
													})}
													locale={datePickerLocale(intl.locale)}
													className={styles.datePickerInput}
													isClearable
													showMonthDropdown
													showYearDropdown
													dropdownMode='scroll'
													scrollableYearDropdown
													yearDropdownItemNumber={15}
													popperPlacement='bottom-end'
													calendarStartDay={1}
												/>
												<Calendar size={16} className={styles.calendarIcon} />
											</div>
										</div>
									</div>

									<div className={styles.datePresetsSection}>
										<div className={styles.datePresets}>
											<button
												type='button'
												className={styles.datePreset}
												onClick={() => handleDatePreset('last30days')}
											>
												{intl.formatMessage({ id: 'TransactionsID14' })}
											</button>
											<button
												type='button'
												className={styles.datePreset}
												onClick={() => handleDatePreset('last7days')}
											>
												{intl.formatMessage({ id: 'TransactionsID15' })}
											</button>
											<button
												type='button'
												className={styles.datePreset}
												onClick={() => handleDatePreset('today')}
											>
												{intl.formatMessage({ id: 'TransactionsID16' })}
											</button>
										</div>
									</div>
								</div>
							</div>

							<div className={styles.filterSection}>
								<h4 className={styles.sectionTitle}>
									{intl.formatMessage({ id: 'TransactionsID17' })}
								</h4>
								<div className={styles.frequencyList}>
									{transactionTypes.map(type => (
										<button
											key={type}
											className={`${styles.frequencyButton} ${
												selectedType === type ? styles.active : ''
											}`}
											onClick={() => handleTypeSelect(type)}
										>
											{typeLabels[type]}
										</button>
									))}
								</div>
							</div>

							<div className={styles.filterSection}>
								<h4 className={styles.sectionTitle}>
									{intl.formatMessage({ id: 'TransactionsID18' })}
								</h4>
								<div className={styles.categoryList}>
									{allCategories.map(cat => {
										const isAll = cat.name === 'All';
										const icon = isAll
											? null
											: getCategoryIcon(cat.original_name || cat.name);
										const isChecked = isAll
											? selectedCategories.includes('All')
											: selectedCategories.includes(cat.name) ||
												selectedCategories.includes('All');

										return (
											<label key={cat.name} className={styles.categoryItem}>
												{!isAll && icon && <img src={icon} alt={cat.name} />}
												{isAll && (
													<div className={styles.iconPlaceholder}></div>
												)}
												<span className={styles.categoryLabel}>{cat.name}</span>
												<div className={styles.checkboxWrapper}>
													<input
														type='checkbox'
														checked={isChecked}
														onChange={() => handleCategoryToggle(cat.name)}
														className={styles.checkbox}
													/>
													<span className={styles.checkmark}></span>
												</div>
											</label>
										);
									})}
								</div>
							</div>

							{/* <div className={styles.filterSection}>
                  <h4 className={styles.sectionTitle}>Frequency</h4>
                  <div className={styles.frequencyList}>
                    {frequencies.map((frequency) => (
                      <button
                        key={frequency}
                        className={`${styles.frequencyButton} ${
                          selectedFrequency === frequency ? styles.active : ""
                        }`}
                        onClick={() => handleFrequencySelect(frequency)}
                      >
                        {frequency}
                      </button>
                    ))}
                  </div>
                </div> */}

							<div className={styles.modalActions}>
								<button
									className={styles.resetButton}
									onClick={handleResetFilters}
								>
									{intl.formatMessage({ id: 'TransactionsID19' })}
								</button>
								<button
									className={styles.applyButton}
									onClick={handleApplyFilters}
								>
									{intl.formatMessage({ id: 'TransactionsID20' })}
								</button>
							</div>
						</div>
					</div>
				)}

				{/* Mobile: card list */}
				<div className={styles.cardsContainer}>
					{filteredTransactions.length > 0 ? (
						filteredTransactions.map((transaction, index) => (
							<div
								key={`card-${transaction.id}-${index}`}
								className={styles.transactionCard}
							>
								<div className={styles.cardTopRow}>
									<span className={styles.cardDate}>{transaction.date}</span>
								</div>
								<div className={styles.cardMainRow}>
									<div className={styles.cardCategory}>
										<img
											src={transaction.icon}
											alt={transaction.category}
											className={styles.cardCategoryIcon}
										/>
										<span>{transaction.category}</span>
									</div>
									<span
										className={`${styles.cardTypeBadge} ${
											transaction.type ===
											intl.formatMessage({ id: 'IncomeId01' })
												? styles.cardTypeIncome
												: styles.cardTypeExpense
										}`}
									>
										{transaction.type}
									</span>
								</div>
								<div className={styles.cardAmountRow}>
									<span
										className={
											transaction.type ===
											intl.formatMessage({ id: 'IncomeId01' })
												? styles.cardAmountIncome
												: styles.cardAmountExpense
										}
									>
										{transaction.type ===
											intl.formatMessage({ id: 'ExpenseId02' }) && '-'}
										{transaction.amount} {transaction.currency}
									</span>
								</div>
							</div>
						))
					) : (
						<div className={styles.emptyTableState}>
							<h2 className={styles.emptyTableTitle}>
								{intl.formatMessage({ id: 'TransactionsID26' })}
							</h2>
							<img
								className={styles.emptyTableImage}
								src={GlassMinus}
								alt='GlassMinus'
							/>
							<p className={styles.emptyTableMessage}>
								{intl.formatMessage({ id: 'TransactionsID27' })}
								<br />
								{intl.formatMessage({ id: 'TransactionsID28' })}
							</p>
							<button
								className={styles.clearFiltersButton}
								onClick={handleClearFilters}
							>
								{intl.formatMessage({ id: 'TransactionsID29' })}
							</button>
						</div>
					)}
				</div>

				{/* Desktop: table */}
				<div className={styles.tableContainer}>
					<div className={styles.tableHeader}>
						<div className={styles.tableHeaderCell}>
							{intl.formatMessage({ id: 'TransactionsID22' })}
						</div>
						<div className={styles.tableHeaderCell}>
							{intl.formatMessage({ id: 'TransactionsID23' })}
						</div>
						<div className={styles.tableHeaderCell}>
							{intl.formatMessage({ id: 'TransactionsID24' })}
						</div>
						<div className={styles.tableHeaderCell}>
							{intl.formatMessage({ id: 'TransactionsID25' })}
						</div>
					</div>
					<div className={styles.tableBody}>
						{filteredTransactions.length > 0 ? (
							filteredTransactions.map((transaction, index) => (
								<div
									key={`${transaction.id}-${index}`}
									className={styles.tableRow}
								>
									<div className={styles.tableCell}>
										<img
											src={transaction.icon}
											alt={transaction.category}
											className={styles.categoryIcon}
										/>
										<span>{transaction.category}</span>
									</div>
									<div className={styles.tableCell}>
										<span
											className={`${styles.typeBadge} ${
												transaction.type ===
												intl.formatMessage({ id: 'IncomeId01' })
													? styles.incomeBadge
													: styles.expenseBadge
											}`}
										>
											{transaction.type}
										</span>
									</div>
									<div className={styles.tableCell}>{transaction.date}</div>
									<div className={styles.tableCell}>
										<span
											className={
												transaction.type ===
												intl.formatMessage({ id: 'IncomeId01' })
													? styles.incomeAmount
													: styles.expenseAmount
											}
										>
											{transaction.amount} {transaction.currency}
										</span>
									</div>
								</div>
							))
						) : (
							<div className={styles.emptyTableState}>
								<h2 className={styles.emptyTableTitle}>
									{intl.formatMessage({ id: 'TransactionsID26' })}
								</h2>
								<img
									className={styles.emptyTableImage}
									src={GlassMinus}
									alt='GlassMinus'
								/>
								<p className={styles.emptyTableMessage}>
									{intl.formatMessage({ id: 'TransactionsID27' })}
									<br />
									{intl.formatMessage({ id: 'TransactionsID28' })}
								</p>
								<button
									className={styles.clearFiltersButton}
									onClick={handleClearFilters}
								>
									{intl.formatMessage({ id: 'TransactionsID29' })}
								</button>
							</div>
						)}
					</div>
				</div>
			</div>

			<AddRecordModal
				open={isModalOpen}
				onClose={() => {
					setIsModalOpen(false);
					// refreshData();
				}}
				onSuccess={() => refreshData()}
				defaultTab='expense'
			/>
		</div>
	);
};

export default Transactions;
